import { useCallback, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  CardContent,
  Link,
  MenuItem,
  menuItemClasses,
  Tooltip,
  Typography as T,
} from "@mui/material";
import useCohorts from "cohorts/hooks/useCohorts";
import useConfiguredDatasets from "cohorts/hooks/useConfiguredDatasets";
import useSensitiveDataConfig from "cohorts/hooks/useSensitiveDataConfig";
import useTotalParticipantsCount from "cohorts/hooks/useTotalParticipantsCount";
import { CohortSummary } from "cohorts/types/interfaces/cohort";
import {
  formatParticipantCount,
  getEffectiveSensitiveDataConfig,
} from "cohorts/utils/formatParticipantCount";
import pluralize from "pluralize";
import { CohortFilledIcon, CohortIcon, DatasetIcon, ErrorIcon } from "~/assets/icons";
import ActionsMenu from "~/components/ActionsMenu";
import CT from "~/components/ClampedTypography";
import InteractiveCard, { IconContainer } from "~/components/InteractiveCard";
import useErrorToast from "~/contexts/error-toast";
import { trackEvent } from "~/utils/monitoring";
import DeleteCohortModal from "./DeleteCohortModal";
import RenameCohortModal from "./RenameCohortModal";

const numFormatter = Intl.NumberFormat("en", { notation: "compact" });

const CohortListCard = ({ cohort }: { cohort: CohortSummary }) => {
  const { showError } = useErrorToast();
  const { mutate } = useCohorts();
  const { data: total } = useTotalParticipantsCount();
  const { data: configuredDatasets } = useConfiguredDatasets();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const participantCount = cohort.participant_count ?? total?.participant_count;
  const description = cohort.user_description || cohort.llm_description;
  const multipleConfiguredDatasets = Number(configuredDatasets?.length) > 1;
  const datasetInfo = configuredDatasets?.find(({ id }) => id === cohort.dataset_id);
  const datasetName = multipleConfiguredDatasets ? datasetInfo?.title : null;
  const isMissingDataset = configuredDatasets && !datasetInfo;
  const { data: sensitiveDataConfig } = useSensitiveDataConfig();

  const { effectiveSensitiveDataConfig } = useMemo(
    () => getEffectiveSensitiveDataConfig(datasetInfo?.access_level, sensitiveDataConfig),
    [datasetInfo?.access_level, sensitiveDataConfig]
  );

  const handleRename = () => {
    setShowActions(false);
    setRenameModalOpen(true);
  };

  const handleDuplicate = useCallback(async () => {
    const resp = await fetch(`/api/cohort-explorer/cohorts/${cohort!.id}/duplicate`, {
      method: "POST",
    });

    if (resp.ok) {
      const body = await resp.json();
      trackEvent("cohort_duplicated", {
        cohort_id: body.id,
        original_cohort_id: cohort!.id,
        dataset_id: cohort!.dataset_id,
      });
      mutate();
    } else {
      showError("Unable to duplicate cohort");
    }
  }, [cohort, mutate, showError]);

  const handleDelete = () => {
    setShowActions(false);
    setDeleteModalOpen(true);
  };

  const menuItems = useMemo(() => {
    const result = [];

    if (!isMissingDataset) {
      result.push(
        <MenuItem key="rename" onClick={handleRename}>
          Rename...
        </MenuItem>,
        <MenuItem key="duplicate" onClick={handleDuplicate}>
          Duplicate
        </MenuItem>
      );
    }

    result.push(
      <MenuItem
        key="delete"
        onClick={handleDelete}
        sx={{
          color: "attention.main",
          [`&.${menuItemClasses.root}:hover`]: {
            color: "common.white",
            bgcolor: "attention.main",
          },
        }}
      >
        Delete
      </MenuItem>
    );
    return result;
  }, [handleDuplicate, isMissingDataset]);

  return (
    <>
      <Link
        // Disable link if dataset is missing
        component={!isMissingDataset ? RouterLink : Box}
        underline="none"
        to={`/cohorts/${String(cohort.id)}`}
        sx={{ display: "flex" }}
      >
        <InteractiveCard
          onMouseEnter={() => {
            setShowActions(true);
          }}
          onMouseLeave={() => {
            setShowActions(false);
          }}
          sx={{
            ...(isMissingDataset ? { cursor: "auto !important", opacity: 0.7 } : {}),
          }}
        >
          <CardContent sx={{ px: 3, pt: 2.5, gap: 1.25, minHeight: 220 }}>
            <Box display="flex" alignItems="center" flex="0" justifyContent="space-between">
              <IconContainer sx={{ bgcolor: "white", color: "primary.main" }}>
                <CohortIcon />
              </IconContainer>
              <Box position="absolute" right={12} top={12}>
                <ActionsMenu
                  disablePortal
                  placement="bottom-start"
                  visible={showActions}
                  menuItems={menuItems}
                />
              </Box>
            </Box>

            <CT variant="body1" sx={{ WebkitLineClamp: 1, lineHeight: 1.2, fontWeight: 600 }}>
              {cohort.name}
            </CT>
            {description && (
              <CT
                variant="body2"
                sx={{ color: "grey.700", fontSize: 12, lineHeight: 1.3, WebkitLineClamp: 3 }}
              >
                {description}
              </CT>
            )}
            <Box
              sx={{
                flex: 1,
                gap: 0.75,
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
              }}
            >
              {isMissingDataset ? (
                <Tooltip
                  arrow
                  title="The dataset associated with this cohort is no longer available"
                >
                  <Box
                    sx={{
                      display: "flex",
                      width: "fit-content",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Box
                      color="warning.main"
                      component={ErrorIcon}
                      width={16}
                      height={16}
                      minWidth={16}
                      minHeight={16}
                    />
                    <T variant="body2" noWrap sx={{ fontSize: 12, opacity: 0.7 }}>
                      Missing dataset
                    </T>
                  </Box>
                </Tooltip>
              ) : (
                datasetName && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Box
                      color="grey.500"
                      component={DatasetIcon}
                      width={16}
                      height={16}
                      minWidth={16}
                      minHeight={16}
                    />
                    <T variant="body2" noWrap sx={{ fontSize: 12, opacity: 0.7 }}>
                      {datasetName}
                    </T>
                  </Box>
                )
              )}
              {participantCount !== undefined && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    component={CohortFilledIcon}
                    width={16}
                    height={16}
                    minWidth={16}
                    minHeight={16}
                    opacity={0.5}
                  />
                  <T variant="body2" sx={{ fontSize: 12, opacity: 0.7 }}>
                    {formatParticipantCount(
                      participantCount,
                      numFormatter,
                      effectiveSensitiveDataConfig
                    )}{" "}
                    {pluralize((cohort.subject_label ?? "").toLowerCase(), participantCount)}
                  </T>
                </Box>
              )}
            </Box>
          </CardContent>
        </InteractiveCard>
      </Link>

      <RenameCohortModal
        id={cohort.id}
        name={cohort.name}
        open={renameModalOpen}
        handleClose={() => setRenameModalOpen(false)}
      />

      <DeleteCohortModal
        cohort={cohort}
        open={deleteModalOpen}
        handleClose={() => setDeleteModalOpen(false)}
      />
    </>
  );
};

export default CohortListCard;
